import { Button, Slide, Snackbar, SnackbarCloseReason, SnackbarOrigin, SxProps, Theme } from '@mui/material';
import { alpha, lighten, Palette } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';

import {
  SNACKBAR_ACTION_TINT,
  SNACKBAR_AUTO_HIDE_MS,
  SNACKBAR_BOTTOM_TRANSITION,
  SNACKBAR_DEFAULT_BOTTOM,
  SNACKBAR_FOOTER_CLEARANCE,
  SNACKBAR_TRANSITION_DURATION,
  SNACKBAR_TRANSITION_EASING,
  SNACKBAR_Z_INDEX,
} from '../../../constants/ui';
import { useFooterVisibility } from '../../../contexts/FooterContext';

interface IProps {
  open: boolean;
  message: string;
  onUndo: () => void;
  onClose: () => void;
}

const anchorOrigin: SnackbarOrigin = { vertical: 'bottom', horizontal: 'left' };

const transitionSlots = { transition: Slide };

const transitionSlotProps = { transition: { direction: 'up' as const, easing: SNACKBAR_TRANSITION_EASING } };

// A lighter tint of the brand primary: the primary itself is unreadable on the dark snackbar.
const getUndoColor = (palette: Palette) => lighten(palette.primary.main, SNACKBAR_ACTION_TINT);

const undoButtonSx: SxProps<Theme> = {
  color: ({ palette }) => getUndoColor(palette),
  '&:hover': { backgroundColor: ({ palette }) => alpha(getUndoColor(palette), 0.08) },
};

export const UndoSnackbar: React.FC<IProps> = ({ open, message, onUndo, onClose }) => {
  const { isFooterVisible } = useFooterVisibility();

  // Rides above the sticky action bar while it is shown, like the FAB.
  const snackbarSx: SxProps<Theme> = useMemo(
    () => ({
      zIndex: SNACKBAR_Z_INDEX,
      // Per breakpoint: MUI's own `sm` bottom sits in a media query that would beat a plain value.
      bottom: isFooterVisible
        ? { xs: SNACKBAR_FOOTER_CLEARANCE, sm: SNACKBAR_FOOTER_CLEARANCE }
        : SNACKBAR_DEFAULT_BOTTOM,
      transition: SNACKBAR_BOTTOM_TRANSITION,
    }),
    [isFooterVisible],
  );

  // A click elsewhere on the page must not discard the chance to undo.
  const handleClose = useCallback(
    (_event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason !== 'clickaway') {
        onClose();
      }
    },
    [onClose],
  );

  const handleUndo = useCallback(() => {
    onUndo();
    onClose();
  }, [onUndo, onClose]);

  const action = useMemo(
    () => (
      <Button size="small" onClick={handleUndo} sx={undoButtonSx}>
        Undo
      </Button>
    ),
    [handleUndo],
  );

  return (
    <Snackbar
      open={open}
      message={message}
      action={action}
      autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
      transitionDuration={SNACKBAR_TRANSITION_DURATION}
      slots={transitionSlots}
      slotProps={transitionSlotProps}
      anchorOrigin={anchorOrigin}
      onClose={handleClose}
      sx={snackbarSx}
    />
  );
};
